<script setup lang="ts">
import { computed } from 'vue';
import Dropdown from '@/_core/shared/ui/Dropdown/Dropdown.vue';
import type { TDropdownItem } from '@/_core/shared/ui/Dropdown/Dropdown.vue';
import type { TFolder } from '@/_core/entities/report/models/types';
import type { TVulnerabilityFolder } from '@/_core/entities/vulnerability/models/types';
import { DEFAULT_FOLDER_NAMES } from '@/_core/entities/report/models/consts';
import FolderMoveIcon from '@/assets/images/svg/folder-move.svg?component';
import ChevronDownIcon from '@/assets/images/svg/chevron-down.svg?component';

const EMIT_MOVE = 'move' as const;
const EMIT_CREATE_FOLDER = 'create-folder' as const;

const props = defineProps<{
  folders: TFolder[];
  currentFolder: TVulnerabilityFolder | null;
  className?: string;
}>();

const emit = defineEmits<{
  (e: typeof EMIT_MOVE, folder: TFolder): void;
  (e: typeof EMIT_CREATE_FOLDER): void;
}>();

const handleCreateFolder = () => emit(EMIT_CREATE_FOLDER);
const handleMove = (folder: TFolder) => emit(EMIT_MOVE, folder);

const STATIC_CREATE_FOLDER_ITEM: TDropdownItem = {
  label: 'Create folder',
  action: handleCreateFolder,
  className: 'tw:text-main-blue',
};

const items = computed<TDropdownItem[]>(() => [
  STATIC_CREATE_FOLDER_ITEM,
  ...props.folders
    .filter((folder) => folder.name !== DEFAULT_FOLDER_NAMES.allVulnerabilities) // virtual folder for all vulnerabilities, should not be included
    .map((folder) => ({
      label: folder.name,
      action: () => handleMove(folder),
    })),
]);
</script>

<template>
  <Dropdown :items="items" :class="className">
    <template #default="{ isOpen }">
      <div
        class="tw:group tw:px-4 tw:py-2.5 tw:flex tw:items-center tw:gap-2.5 tw:text-ghost tw:cursor-pointer tw:border tw:border-[#E3E6ED] tw:rounded-lg tw:select-none"
      >
        <div class="tw:max-w-6"><FolderMoveIcon /></div>
        <span
          class="tw:typography-control tw:block tw:group-hover:hidden"
          :class="{ 'tw:hidden!': isOpen }"
          >{{ currentFolder?.name ?? 'Main Report' }}</span
        >
        <span
          class="tw:typography-control tw:hidden tw:group-hover:block"
          :class="{ 'tw:block!': isOpen }"
          >Move To</span
        >
        <div
          class="tw:size-5 tw:ml-auto tw:transition-transform"
          :class="{ 'tw:rotate-180': isOpen }"
        >
          <ChevronDownIcon />
        </div>
      </div>
    </template>
  </Dropdown>
</template>
